import { useEffect, useId, useState } from "react";
import * as Sentry from "@sentry/react";

import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { Spinner } from "@/components/ui/spinner";
import { cn } from "@/lib/utils";
import { listDrafts } from "../../../api/drafts-v2";
import { Draft, DRAFT_TYPE_LABELS, DraftType } from "../../../api/types/draft";

export type SaveDestination =
  | { kind: "existing"; draftId: string; label: string }
  | { kind: "custom"; title: string };

type Props = {
  isOpen: boolean;
  individualId: string | null;
  isSaving: boolean;
  onCancel: () => void;
  onConfirm: (destination: SaveDestination) => void;
};

const NEW_CUSTOM = "__new_custom__";
// Structured artifact; not a sensible destination for freeform Athena content.
const EXCLUDED_TYPES = new Set<DraftType>([DraftType.EXHIBIT_LIST]);

const draftLabel = (draft: Draft): string => {
  const title = draft.title?.trim();
  return title || DRAFT_TYPE_LABELS[draft.draft_type] || "Draft";
};

export function SaveToDraftDestinationModal({
  isOpen,
  individualId,
  isSaving,
  onCancel,
  onConfirm
}: Props) {
  const [drafts, setDrafts] = useState<Draft[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [selectedId, setSelectedId] = useState<string>(NEW_CUSTOM);
  const [customTitle, setCustomTitle] = useState("Custom Draft");
  const baseId = useId();

  useEffect(() => {
    if (!isOpen) return;
    setSelectedId(NEW_CUSTOM);
    setCustomTitle("Custom Draft");
  }, [isOpen]);

  useEffect(() => {
    if (!isOpen || !individualId) return undefined;
    let cancelled = false;
    setIsLoading(true);
    listDrafts({ individual_id: individualId })
      .then((list) => {
        if (cancelled) return;
        setDrafts(list.filter((d) => !EXCLUDED_TYPES.has(d.draft_type)));
      })
      .catch((e) => {
        Sentry.captureException(e);
        if (!cancelled) setDrafts([]);
      })
      .finally(() => {
        if (!cancelled) setIsLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [isOpen, individualId]);

  const canConfirm =
    selectedId === NEW_CUSTOM
      ? customTitle.trim().length > 0
      : Boolean(selectedId);

  const handleConfirm = () => {
    if (selectedId === NEW_CUSTOM) {
      onConfirm({
        kind: "custom",
        title: customTitle.trim() || "Custom Draft"
      });
      return;
    }
    const draft = drafts.find((d) => d.draft_id === selectedId);
    onConfirm({
      kind: "existing",
      draftId: selectedId,
      label: draft ? draftLabel(draft) : "the draft"
    });
  };

  const rowClass = (active: boolean): string =>
    cn(
      "flex w-full cursor-pointer items-center gap-3 rounded-lg px-3 py-2.5 inset-ring transition-colors duration-100",
      active
        ? "bg-accent-soft inset-ring-accent-line"
        : "inset-ring-line-2 hover:bg-fill-1"
    );

  return (
    <Dialog
      open={isOpen}
      onOpenChange={(next) => {
        if (!next && !isSaving) onCancel();
      }}
    >
      <DialogContent showCloseButton={false} className="sm:max-w-130">
        <DialogHeader>
          <DialogTitle>Save to Draft</DialogTitle>
          <DialogDescription>
            Choose where to save this reply.
          </DialogDescription>
        </DialogHeader>

        <RadioGroup
          value={selectedId}
          onValueChange={setSelectedId}
          className="flex max-h-80 flex-col gap-2 overflow-y-auto p-px"
        >
          <Label
            htmlFor={`${baseId}-${NEW_CUSTOM}`}
            className={rowClass(selectedId === NEW_CUSTOM)}
          >
            <RadioGroupItem id={`${baseId}-${NEW_CUSTOM}`} value={NEW_CUSTOM} />
            <span className="flex min-w-0 flex-1 flex-col gap-0.5">
              <span className="text-base font-medium text-fg">
                New Custom Draft
              </span>
              <span className="text-sm font-normal text-fg-3">
                Creates a new Custom card on the case.
              </span>
            </span>
          </Label>

          {selectedId === NEW_CUSTOM ? (
            <Input
              aria-label="Draft title"
              placeholder="Draft title"
              value={customTitle}
              onChange={(e) => setCustomTitle(e.target.value)}
            />
          ) : null}

          {isLoading ? (
            <span className="flex items-center gap-2 px-1 py-2 text-sm text-fg-3">
              <Spinner className="size-3.5" />
              Loading drafts…
            </span>
          ) : drafts.length > 0 ? (
            <>
              <span className="px-1 pt-1 text-sm text-fg-3">
                Or Replace an Existing Draft
              </span>
              {drafts.map((draft) => {
                const active = selectedId === draft.draft_id;
                const itemId = `${baseId}-${draft.draft_id}`;
                return (
                  <Label
                    key={draft.draft_id}
                    htmlFor={itemId}
                    className={rowClass(active)}
                  >
                    <RadioGroupItem id={itemId} value={draft.draft_id} />
                    <span className="flex min-w-0 flex-1 flex-col gap-0.5">
                      <span className="truncate text-base font-medium text-fg">
                        {draftLabel(draft)}
                      </span>
                      <span className="text-sm font-normal text-fg-3">
                        {DRAFT_TYPE_LABELS[draft.draft_type]}
                      </span>
                    </span>
                  </Label>
                );
              })}
            </>
          ) : null}
        </RadioGroup>

        <p className="text-sm text-fg-3">
          Saving into an existing draft replaces its content; the previous
          version is kept in the draft&apos;s history.
        </p>

        <DialogFooter>
          <Button variant="secondary" onClick={onCancel} disabled={isSaving}>
            Cancel
          </Button>
          <Button
            variant="primary"
            onClick={handleConfirm}
            disabled={isSaving || !canConfirm}
          >
            {isSaving ? <Spinner className="size-3.5" /> : null}
            Save
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export default SaveToDraftDestinationModal;
